import { UserRoleTypeUpperCase, ViEvent } from '@vi-iot/client';

// https://wiki.viessmann-climatesolutions.com/display/EWV/Response+model%3A+Event+entry
export enum EventTypes {
  DEVICE_BMU_SERIAL_CHANGED = 'device-bmu-serial-changed',
  DEVICE_BOILER_SERIAL_CHANGED = 'device-boiler-serial-changed',
  DEVICE_ERROR = 'device-error',
  DEVICE_MESSAGE_INFO = 'device-message-info',
  DEVICE_MESSAGE_SERVICE = 'device-message-service',
  DEVICE_MESSAGE_STATUS = 'device-message-status',
  DEVICE_MESSAGE_WARNING = 'device-message-warning',
  GATEWAY_ONLINE = 'gateway-online',
  GATEWAY_RESET_COMMAND = 'gateway-reset-command',
  GEOFENCING_ACTIVATION_CHANGED = 'geofencing-activation-changed',
  FIRMWARE_UPDATE_STATUS = 'firmware-update-status',
  POTA_UPDATE_STATUS = 'pota-update-status',
  FEATURE_CHANGED = 'feature-changed',
  INSTALLATION_ADDRESS_CHANGED = 'installation-address-changed',
  INVITATION_ACCEPTED = 'invitation-accepted',
  INVITATION_TERMINATED = 'invitation-terminated',
  SNAPSHOT_RESTORE_STATUS_CHANGED = 'snapshot-restore-status-changed',
  SRC_STATUS_CHANGED = 'src-status-changed',
  HEATING_ROD_RUN_TOO_LONG = 'diy-heating-rod-run-too-long',
  VIFLEX = 'viFlex-sg-ready-change',
  DYNAMIC_SAVING_TIPS_HEATINGCURVE_SAVINGS = 'dynamic_saving_tips_heatingcurve_savings',
  SETBACK_DURATION_CAN_BE_INCREASED_BY_ONE_HOUR = 'setback_duration_can_be_increased_by_one_hour',
  DHW_TARGET_TEMPERATURE_CAN_BE_DECREASED = 'dhw_target_temperature_can_be_decreased',
  DHW_DURATION_CAN_BE_DECREASED = 'dhw_duration_can_be_decreased',
  EXTERNAL_LOCK = 'external-lock',
  EXTERNAL_DEMAND = 'external-demand',
}

export enum EventGroups {
  SYSTEM_MESSAGES = 'system-messages',
  COMMUNICATION_MODULE = 'communication-module',
  SETTINGS = 'settings',
  INSTALLATION_DATA = 'installation-data',
  INVITATIONS = 'invitations',
}

export enum EventUpdateStatus {
  FAILED = 'Failed',
  COMPLETED = 'Completed',
  STARTED = 'Started',
}

export const EventLogGroups = [
  {
    name: EventGroups.SYSTEM_MESSAGES,
    events: [
      EventTypes.DEVICE_ERROR,
      EventTypes.DEVICE_MESSAGE_WARNING,
      EventTypes.DEVICE_MESSAGE_SERVICE,
      EventTypes.DEVICE_MESSAGE_INFO,
      EventTypes.DEVICE_MESSAGE_STATUS,
      EventTypes.HEATING_ROD_RUN_TOO_LONG,
      EventTypes.VIFLEX,
      EventTypes.DYNAMIC_SAVING_TIPS_HEATINGCURVE_SAVINGS,
    ],
  },
  {
    name: EventGroups.COMMUNICATION_MODULE,
    events: [
      EventTypes.GATEWAY_ONLINE,
      EventTypes.FIRMWARE_UPDATE_STATUS,
      EventTypes.GATEWAY_RESET_COMMAND,
    ],
  },
  {
    name: EventGroups.SETTINGS,
    events: [
      EventTypes.FEATURE_CHANGED,
      EventTypes.SNAPSHOT_RESTORE_STATUS_CHANGED,
      EventTypes.SRC_STATUS_CHANGED,
      EventTypes.GEOFENCING_ACTIVATION_CHANGED,
      EventTypes.EXTERNAL_LOCK,
      EventTypes.EXTERNAL_DEMAND,
    ],
  },
  {
    name: EventGroups.INSTALLATION_DATA,
    events: [
      EventTypes.DEVICE_BMU_SERIAL_CHANGED,
      EventTypes.DEVICE_BOILER_SERIAL_CHANGED,
      EventTypes.INSTALLATION_ADDRESS_CHANGED,
    ],
  },
  {
    name: EventGroups.INVITATIONS,
    events: [EventTypes.INVITATION_ACCEPTED, EventTypes.INVITATION_TERMINATED],
  },
];

export interface EventsData {
  data: ViEvent[];
  cursor: { next: string };
}

export interface CachedUserDetails {
  uuid: string;
  username: string;
  userRole: UserRoleTypeUpperCase;
}

export interface NextChunkLink {
  next: string;
}

export interface LoadEventsActionParams {
  gatewaySerials: string;
  limit: number;
}

export interface ErrorEvent {
  eventType: EventTypes;
  gatewaySerial: string;
  createdAt: string;
  eventTimestamp: string;
  editedBy: string;
  body: {
    active: boolean;
    deviceId: string;
    modelId: string;
    errorCode: string;
    errorEventType: string;
    errorMessage: string;
    errorDescription: string;
    equipmentType: string;
  };
}

export interface NotificationEvent {
  eventType: EventTypes;
  gatewaySerial: string;
  createdAt: string;
  eventTimestamp: string;
  editedBy: string;
  body: {
    updateStatus?: string;
    online?: boolean;
    featureName?: string;
    boilerSerial?: string;
    bmuSerial?: string;
    deviceId?: string;
    editor?: string;
    modelId?: string;
    caller?: string;
    errorCode?: string;
    active?: boolean;
    inviter?: string;
    invitee?: string;
    startTime?: string;
    endTime?: string;
    savingPotentialAbsolute?: number;
    savingPotentialRelative?: number;
    temperatureRecommended?: number;
    temperatureCurrent?: number;
    durationRecommended?: number;
    durationCurrent?: number;
    commandName?: string;
    commandBody?: {
      mode?: string;
      temperature?: number;
      targetTemperature?: number;
      hysteresis?: number;
      actorDeviceId?: string;
      id?: string;
      name?: string;
      value?: string;
      start?: string;
      end?: string;
      version?: string;
      url?: string;
      type?: string;
    };
    invitationStatus?: EventTypes.INVITATION_ACCEPTED | EventTypes.INVITATION_TERMINATED;
    firmwareVersion?: {
      build: number;
      major: number;
      majorRevision: number;
      minor: number;
      minorRevision: number;
      revision: number;
    };
  };
  origin?: string;
}

export interface Events {
  [key: string]: ViEvent[];
}

export interface Cursors {
  [key: string]: string;
}

export const END_OF_LIST = 'end-of-list';
